import { CONFIG } from '../config.js';
import { getOverall } from '../storage.js';
import { isMuted, toggleMuted } from './sounds.js';
import type { Country, EffectiveQuizMode, Question, QuizPhase, QuizState } from '../types.js';

export interface QuizPanelHandlers {
  onPick: (country: Country) => void;
  onRestart: () => void;
  onMenu: () => void;
  onOpenStats: () => void;
  /** 결과 표시 중(correct/wrong) 화면 탭으로 다음 문제 즉시 진행. */
  onSkipDelay?: () => void;
}

// flagcdn.com — ISO 3166-1 alpha-2 lowercase. hNNN = 높이 NNN px PNG.
function flagUrl(iso2: string, size: 'h40' | 'h80' | 'h120' | 'h240' = 'h120'): string {
  return `https://flagcdn.com/${size}/${iso2.toLowerCase()}.png`;
}

export class QuizPanel {
  private readonly root: HTMLElement;
  private readonly scoreEl: HTMLElement;
  private readonly progressEl: HTMLElement;
  private readonly comboEl: HTMLElement;
  private readonly comboItem: HTMLElement;
  private readonly comboBurst: HTMLElement;
  private readonly timerBar: HTMLElement;
  private readonly timerWrap: HTMLElement;
  private readonly promptWrap: HTMLElement;
  private readonly promptText: HTMLElement;
  private readonly promptHint: HTMLElement;
  private readonly flagImg: HTMLImageElement;
  private readonly flagFallback: HTMLElement;
  private readonly choices: HTMLElement;
  private readonly feedback: HTMLElement;
  private readonly finish: HTMLElement;
  private readonly modeToast: HTMLElement;
  private readonly muteBtn: HTMLButtonElement;
  private handlers: QuizPanelHandlers | null = null;
  private buttons: HTMLButtonElement[] = [];
  private lastCombo = 0;
  private currentPhase: QuizPhase = 'idle';
  private lastMode: EffectiveQuizMode = 'name';

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.innerHTML = `
      <div class="header">
        <div class="header-item"><span class="label">점수</span><span class="score">0</span></div>
        <div class="header-item"><span class="label">문제</span><span class="progress">0 / 0</span></div>
        <div class="header-item combo-item" hidden><span class="label">콤보</span><span class="combo">0</span></div>
        <button type="button" class="mute-btn" aria-label="음소거 토글">🔊</button>
        <button type="button" class="menu-btn" aria-label="메뉴">⋯</button>
      </div>
      <div class="timer-wrap" hidden>
        <div class="timer-bar"></div>
      </div>
      <div class="prompt-wrap">
        <img class="flag-img" alt="" hidden />
        <span class="flag-fallback" hidden>🏳️</span>
        <div class="prompt-text">이 나라 이름은?</div>
        <div class="prompt-hint" hidden>👇 노란 나라 중에서 골라봐</div>
      </div>
      <div class="choices"></div>
      <div class="feedback" hidden></div>
      <div class="combo-burst" hidden></div>
      <div class="mode-toast" hidden></div>
      <div class="finish" hidden></div>
    `;
    this.scoreEl = this.must('.score');
    this.progressEl = this.must('.progress');
    this.comboEl = this.must('.combo');
    this.comboItem = this.must('.combo-item');
    this.comboBurst = this.must('.combo-burst');
    this.timerWrap = this.must('.timer-wrap');
    this.timerBar = this.must('.timer-bar');
    this.promptWrap = this.must('.prompt-wrap');
    this.promptText = this.must('.prompt-text');
    this.promptHint = this.must('.prompt-hint');
    this.flagImg = this.must<HTMLImageElement>('.flag-img');
    this.flagFallback = this.must('.flag-fallback');
    this.flagImg.addEventListener('error', () => {
      this.flagImg.hidden = true;
      this.flagFallback.hidden = false;
    });
    this.choices = this.must('.choices');
    this.feedback = this.must('.feedback');
    this.finish = this.must('.finish');
    this.modeToast = this.must('.mode-toast');
    this.muteBtn = this.must<HTMLButtonElement>('.mute-btn');
    this.muteBtn.textContent = isMuted() ? '🔇' : '🔊';
    this.muteBtn.addEventListener('click', () => {
      const nowMuted = toggleMuted();
      this.muteBtn.textContent = nowMuted ? '🔇' : '🔊';
    });
    const menuBtn = this.must<HTMLButtonElement>('.menu-btn');
    menuBtn.addEventListener('click', () => {
      // 라운드 진행 중(asking/correct/wrong) 메뉴로 나가면 점수가 사라지므로 확인.
      const inRound =
        this.currentPhase === 'asking' ||
        this.currentPhase === 'correct' ||
        this.currentPhase === 'wrong';
      if (inRound && !window.confirm('지금 그만둘까? 진행 중인 점수는 사라져')) {
        return;
      }
      this.handlers?.onMenu();
    });

    // 결과 표시 중 화면 어디든 탭하면 다음 문제로 즉시 진행.
    // 헤더 버튼·선택지·결과 화면·메뉴·기록 패널 클릭은 제외.
    document.addEventListener('click', (e) => {
      if (this.currentPhase !== 'correct' && this.currentPhase !== 'wrong') return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest('.menu-btn') ||
        target.closest('.mute-btn') ||
        target.closest('.choice') ||
        target.closest('.finish') ||
        target.closest('.start-menu') ||
        target.closest('.stats-panel')
      ) {
        return;
      }
      this.handlers?.onSkipDelay?.();
    });
  }

  setHandlers(h: QuizPanelHandlers): void {
    this.handlers = h;
  }

  applyOptionsForRound(): void {
    this.timerWrap.hidden = !CONFIG.timeLimit.enabled;
  }

  renderState(state: QuizState): void {
    this.currentPhase = state.phase;
    this.scoreEl.textContent = String(state.score);
    this.progressEl.textContent = `${Math.min(
      state.questionIndex + (state.phase === 'finished' ? 0 : 1),
      state.totalQuestions,
    )} / ${state.totalQuestions}`;

    this.comboEl.textContent = String(state.combo);
    const showCombo = state.combo >= 2;
    this.comboItem.hidden = !showCombo;
    if (state.combo > this.lastCombo && state.combo >= 3) {
      this.flashCombo(state.combo);
    }
    this.lastCombo = state.combo;
  }

  renderQuestion(q: Question, mode: EffectiveQuizMode, modeChanged = false): void {
    this.lastMode = mode;
    this.feedback.hidden = true;
    this.flagFallback.hidden = true;
    this.flagImg.hidden = false;
    this.flagImg.src = flagUrl(q.answer.iso2, 'h240');
    this.flagImg.alt = '출제 국가 국기';
    this.choices.innerHTML = '';
    this.buttons = [];
    if (modeChanged) this.showModeToast(mode);
    if (mode === 'location') {
      // 위치 모드: 정답 국가 이름을 prompt에 큼지막하게 노출, 보기 카드는 숨김.
      this.promptText.innerHTML = `<strong class="prompt-country">${escapeHtml(q.answer.nameKo)}</strong> <span class="prompt-question">— 어디일까?</span>`;
      this.promptHint.hidden = false;
      this.choices.hidden = true;
      return;
    }
    this.promptText.textContent = '이 나라 이름은?';
    this.promptHint.hidden = true;
    this.choices.hidden = false;
    this.buttons = q.choices.map((country) => {
      const btn = document.createElement('button');
      btn.className = 'choice';
      btn.type = 'button';
      btn.textContent = country.nameKo;
      btn.dataset.iso2 = country.iso2;
      btn.addEventListener('click', () => {
        this.handlers?.onPick(country);
      });
      this.choices.appendChild(btn);
      return btn;
    });
  }

  renderResult(correct: boolean, picked: Country, answer: Country): void {
    this.feedback.hidden = false;
    this.feedback.classList.toggle('correct', correct);
    this.feedback.classList.toggle('wrong', !correct);
    const flagHtml = `<img class="feedback-flag" src="${flagUrl(answer.iso2, 'h80')}" alt="" onerror="this.style.display='none'" />`;
    if (correct) {
      this.feedback.innerHTML = `${flagHtml}<span>정답! ${escapeHtml(answer.nameKo)}</span>`;
    } else {
      const pickedTxt = picked.iso2 === answer.iso2 ? '시간 초과' : picked.nameKo;
      this.feedback.innerHTML = `${flagHtml}<span>${escapeHtml(pickedTxt)} → 정답: ${escapeHtml(answer.nameKo)}</span>`;
    }
    for (const btn of this.buttons) {
      btn.disabled = true;
      const iso = btn.dataset.iso2;
      if (iso === answer.iso2) btn.classList.add('answer');
      if (iso === picked.iso2 && !correct) btn.classList.add('picked-wrong');
    }
  }

  renderTimer(remaining: number): void {
    if (!CONFIG.timeLimit.enabled) return;
    this.timerBar.style.width = `${Math.max(0, Math.min(1, remaining)) * 100}%`;
    this.timerBar.classList.toggle('danger', remaining < 0.25);
  }

  renderFinish(state: QuizState): void {
    this.choices.innerHTML = '';
    this.feedback.hidden = true;
    this.promptText.textContent = '';
    this.flagImg.hidden = true;
    this.flagImg.removeAttribute('src');
    this.comboItem.hidden = true;
    this.finish.hidden = false;
    const pct = state.totalQuestions === 0
      ? 0
      : Math.round((state.score / state.totalQuestions) * 100);
    const wrongList = state.history.filter((h) => !h.correct);
    const wrongHtml = wrongList.length === 0
      ? '<p class="result-perfect">한 문제도 안 틀렸어! 👏</p>'
      : `<div class="wrong-list">
          <div class="wrong-list-title">놓친 나라</div>
          <ul>${wrongList
            .map((h) => {
              const c = h.question.answer;
              return `<li><img class="wrong-flag" src="${flagUrl(c.iso2, 'h40')}" alt="" onerror="this.style.display='none'" /><span>${escapeHtml(c.nameKo)}</span></li>`;
            })
            .join('')}</ul>
        </div>`;
    const overall = getOverall();
    const overallPct = overall.totalQuestions === 0
      ? 0
      : Math.round((overall.totalCorrect / overall.totalQuestions) * 100);
    const overallHtml = overall.rounds === 0
      ? ''
      : `<p class="result-overall">누적: ${overall.rounds} 라운드, 평균 정답률 ${overallPct}%</p>`;
    const bestHtml = renderBestHtml(state);
    this.finish.innerHTML = `
      <div class="finish-card">
        <h2>끝!</h2>
        <p class="result-score">${state.score} / ${state.totalQuestions} <span class="result-pct">(${pct}%)</span></p>
        ${bestHtml}
        <div class="result-stats">
          <div><span class="stat-label">최고 콤보</span><span class="stat-val">${state.maxCombo}</span></div>
          <div><span class="stat-label">틀린 횟수</span><span class="stat-val">${state.wrongCount}</span></div>
        </div>
        <p class="result-comment">${commentFor(pct)}</p>
        ${overallHtml}
        ${wrongHtml}
        <div class="finish-actions">
          <button class="restart" type="button">다시 하기</button>
          <button class="to-menu" type="button">메뉴로</button>
          <button class="to-stats" type="button">내 기록</button>
        </div>
      </div>
    `;
    const restart = this.finish.querySelector<HTMLButtonElement>('.restart');
    restart?.addEventListener('click', () => {
      this.finish.hidden = true;
      this.handlers?.onRestart();
    });
    const toMenu = this.finish.querySelector<HTMLButtonElement>('.to-menu');
    toMenu?.addEventListener('click', () => {
      this.finish.hidden = true;
      this.handlers?.onMenu();
    });
    const toStats = this.finish.querySelector<HTMLButtonElement>('.to-stats');
    toStats?.addEventListener('click', () => {
      this.handlers?.onOpenStats();
    });
  }

  hideFinish(): void {
    this.finish.hidden = true;
  }

  resetCombo(): void {
    this.lastCombo = 0;
    this.comboItem.hidden = true;
  }

  /** mixed 모드에서 이전 문제와 모드가 달라질 때 짧은 토스트로 안내. */
  private showModeToast(mode: EffectiveQuizMode): void {
    const label = mode === 'name' ? '🔀 이름 맞히기 →' : '🔀 위치 맞히기 →';
    this.modeToast.textContent = label;
    this.modeToast.hidden = false;
    this.modeToast.classList.remove('show');
    // force reflow so animation restarts
    void this.modeToast.offsetWidth;
    this.modeToast.classList.add('show');
    window.setTimeout(() => {
      this.modeToast.hidden = true;
      this.modeToast.classList.remove('show');
    }, 420);
  }

  /** 위치 모드에서 보기 외 폴리곤을 클릭했을 때 prompt 영역에 짧은 흔들림. */
  shakePrompt(): void {
    this.promptWrap.classList.remove('shake');
    // force reflow so animation restarts
    void this.promptWrap.offsetWidth;
    this.promptWrap.classList.add('shake');
    window.setTimeout(() => this.promptWrap.classList.remove('shake'), 200);
  }

  private flashCombo(combo: number): void {
    this.comboBurst.textContent = `🔥 ${combo} 콤보!`;
    // 위치 모드에선 burst 위치를 위로 올려 정답 폴리곤이 가려지지 않게.
    this.comboBurst.classList.toggle('in-location', this.lastMode === 'location');
    this.comboBurst.hidden = false;
    this.comboBurst.classList.remove('show');
    // force reflow so animation restarts
    void this.comboBurst.offsetWidth;
    this.comboBurst.classList.add('show');
    window.setTimeout(() => {
      this.comboBurst.hidden = true;
    }, 900);
  }

  private must<T extends Element = HTMLElement>(selector: string): T {
    const el = this.root.querySelector<T>(selector);
    if (!el) throw new Error(`UI 요소 누락: ${selector}`);
    return el;
  }
}

function renderBestHtml(state: QuizState): string {
  const b = state.bestUpdate;
  if (!b) return '';
  if (b.isNew) {
    return `<p class="result-best-badge-line"><span class="result-best-badge">🏆 베스트!</span></p>`;
  }
  if (b.previous !== null) {
    return `<p class="result-best-line">베스트 ${b.previous} / ${state.totalQuestions}</p>`;
  }
  return '';
}

function commentFor(pct: number): string {
  if (pct === 100) return '완벽해! 🌍';
  if (pct >= 80) return '아주 잘했어!';
  if (pct >= 60) return '잘했어!';
  if (pct >= 40) return '조금만 더 연습!';
  return '다시 도전해보자!';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return ch;
    }
  });
}
